import { GUI } from 'lil-gui';
import * as THREE from '@phdsoft/three';
import { GraphicsMaterials } from './graphics-materials';

export interface PolygonOffsetDebugConfig {
  enabled: boolean;
  factor: number;
  units: number;
}

export class GraphicsDebug {
  private gui: GUI;
  private materialsFolder: GUI;
  private polygonOffsetFolder: GUI;
  private debugConfig: { [materialName: string]: PolygonOffsetDebugConfig } = {};
  private sceneMaterials: Map<string, THREE.Material[]> = new Map();
  private scene: THREE.Scene | null = null;

  // Mapping between property names and actual scene material names
  private materialNameMapping: { [propName: string]: string[] } = {
    // Basic Materials
    'basicMaterial': ['BasicMaterial', 'basicMaterial'],
    'basicPhysicalMaterial': ['BasicPhysicalMaterial', 'basicPhysicalMaterial'],
    'lightModelMaterial': ['LightModelMaterial', 'lightModelMaterial'],
    'pointMaterial': ['PointMaterial', 'pointMaterial'],
    'pointMaterialGreen': ['PointMaterialGreen', 'pointMaterialGreen'],
    'pointMaterialYellow': ['PointMaterialYellow', 'pointMaterialYellow'],
    'pointMaterialBlue': ['PointMaterialBlue', 'pointMaterialBlue'],
    'pointMaterialRed': ['PointMaterialRed', 'pointMaterialRed'],
    'pointMaterialBlack': ['PointMaterialBlack', 'pointMaterialBlack'],
    'structure4DMaterial': ['Structure4DMaterial', 'structure4DMaterial'],

    // Inspection Materials
    'inspectionMaterial': ['InspectionMaterial', 'inspectionMaterial'],
    'redInspectionMaterial': ['RedInspectionMaterial', 'redInspectionMaterial'],
    'yellowInspectionMaterial': ['YellowInspectionMaterial', 'yellowInspectionMaterial'],
    'blueInspectionMaterial': ['BlueInspectionMaterial', 'blueInspectionMaterial'],
    'greenInspectionMaterial': ['GreenInspectionMaterial', 'greenInspectionMaterial'],
    'greyInspectionMaterial': ['GreyInspectionMaterial', 'greyInspectionMaterial'],

    // Anomaly Materials
    'greyAnomalyMaterial': ['GreyAnomalyMaterial', 'greyAnomalyMaterial'],
    'redAnomalyMaterial': ['RedAnomalyMaterial', 'redAnomalyMaterial'],
    'orangeAnomalyMaterial': ['OrangeAnomalyMaterial', 'orangeAnomalyMaterial'],
    'yellowAnomalyMaterial': ['YellowAnomalyMaterial', 'yellowAnomalyMaterial'],
    'blueAnomalyMaterial': ['BlueAnomalyMaterial', 'blueAnomalyMaterial'],
    'greenAnomalyMaterial': ['GreenAnomalyMaterial', 'greenAnomalyMaterial'],
    'anomalyGraphicsBasicMaterial': ['AnomalyGraphicsBasicMaterial', 'anomalyGraphicsBasicMaterial'],

    // Risk Materials
    'redRiskMaterial': ['RedRiskMaterial', 'redRiskMaterial'],
    'orangeRiskMaterial': ['OrangeRiskMaterial', 'orangeRiskMaterial'],
    'yellowRiskMaterial': ['YellowRiskMaterial', 'yellowRiskMaterial'],
    'greenRiskMaterial': ['GreenRiskMaterial', 'greenRiskMaterial'],

    // Structure Materials
    'stiffenerMaterial3d': ['StiffenerMaterial3d', 'stiffenerMaterial3d'],
    'bracketMaterial3d': ['BracketMaterial3d', 'bracketMaterial3d'],
    'flangeMaterial3d': ['FlangeMaterial3d', 'flangeMaterial3d'],
    'beamMaterial3d': ['BeamMaterial3d', 'beamMaterial3d'],

    // Selection Materials
    'selectedBasicMaterial': ['SelectedBasicMaterial', 'selectedBasicMaterial'],
    'selectedBasicElementMaterial': ['SelectedBasicElementMaterial', 'selectedBasicElementMaterial'],
    'selectedAreaInspecMaterial': ['SelectedAreaInspecMaterial', 'selectedAreaInspecMaterial'],

    // Special Materials
    'paintMaterial': ['PaintMaterial', 'paintMaterial'],
    'anodeMaterial': ['AnodeMaterial', 'anodeMaterial'],
    'anodeHighlightMaterial': ['AnodeHighlightMaterial', 'anodeHighlightMaterial'],
    'anodeLowlightMaterial': ['AnodeLowlightMaterial', 'anodeLowlightMaterial'],
    'protectionElementsMaterial': ['ProtectionElementsMaterial', 'protectionElementsMaterial'],

    // Critical Area Materials
    'criticalAreaMaterial': ['CriticalAreaMaterial', 'criticalAreaMaterial'],
    'criticalAreaBoxMaterial': ['CriticalAreaBoxMaterial', 'criticalAreaBoxMaterial'],

    // Repair Materials
    'repairBasicMaterial': ['RepairBasicMaterial', 'repairBasicMaterial'],
    'executedRepairMaterial': ['ExecutedRepairMaterial', 'executedRepairMaterial'],
    'doublerExecutedRepairMaterial': ['DoublerExecutedRepairMaterial', 'doublerExecutedRepairMaterial'],
    'cutPreviewMaterial': ['CutPreviewMaterial', 'cutPreviewMaterial'],

    // Sensor Materials
    'sensorMaterial': ['SensorMaterial', 'sensorMaterial'],
    'sensorHighlightMaterial': ['SensorHighlightMaterial', 'sensorHighlightMaterial'],
    'sensorLowlightMaterial': ['SensorLowlightMaterial', 'sensorLowlightMaterial'],
    'sensorXrayMaterial': ['SensorXrayMaterial', 'sensorXrayMaterial'],
    'sensorEmptyBarMaterial': ['SensorEmptyBarMaterial', 'sensorEmptyBarMaterial'],

    // Area Materials
    'areaInspecMaterial': ['AreaInspecMaterial', 'areaInspecMaterial'],
    'inspectionListElementMaterial': ['InspectionListElementMaterial', 'inspectionListElementMaterial'],

    // Other Materials
    'stainlessSteelMaterial': ['StainlessSteelMaterial', 'stainlessSteelMaterial'],
    'decalMaterial': ['DecalMaterial', 'decalMaterial'],
    'rulerLineMaterial': ['RulerLineMaterial', 'rulerLineMaterial'],
    'hatchingMaterial': ['HatchingMaterial', 'hatchingMaterial'],
    'feaMeshMaterial': ['FeaMeshMaterial', 'feaMeshMaterial'],
    'femMaterial': ['FemMaterial', 'femMaterial']
  };

  constructor(scene?: THREE.Scene) {
    this.scene = scene || null;
    this.initializeGUI();
    this.setupPolygonOffsetControls();

    // Start hidden by default
    this.hide();
  }

  private initializeGUI(): void {
    this.gui = new GUI();
    this.gui.title('Three.js Debug Controls');

    // Create folders for organization
    this.materialsFolder = this.gui.addFolder('Materials');
    this.polygonOffsetFolder = this.materialsFolder.addFolder('Polygon Offset');
    this.polygonOffsetFolder.open();
  }

  private setupPolygonOffsetControls(): void {
    // Get all static materials from GraphicsMaterials that use polygonOffset
    const materialsWithPolygonOffset = this.getMaterialsWithPolygonOffset();

    materialsWithPolygonOffset.forEach(({ name, material }) => {
      this.addPolygonOffsetControl(name, material);
    });

    // Add global controls
    this.addGlobalControls();
  }

  private getMaterialsWithPolygonOffset(): { name: string; material: THREE.Material }[] {
    const materials: { name: string; material: THREE.Material }[] = [];

    // Get all static properties from GraphicsMaterials class
    const materialProperties = [
      'commonPointMatProp',
      'inspectionMaterial',
      'pointMaterial',
      'pointMaterialGreen',
      'pointMaterialYellow',
      'pointMaterialBlue',
      'pointMaterialRed',
      'pointMaterialBlack',
      'redInspectionMaterial',
      'yellowInspectionMaterial',
      'blueInspectionMaterial',
      'greenInspectionMaterial',
      'greyInspectionMaterial',
      'greyAnomalyMaterial',
      'redAnomalyMaterial',
      'orangeAnomalyMaterial',
      'yellowAnomalyMaterial',
      'blueAnomalyMaterial',
      'greenAnomalyMaterial',
      'redRiskMaterial',
      'orangeRiskMaterial',
      'yellowRiskMaterial',
      'greenRiskMaterial',
      'paintMaterial',
      'anodeMaterial',
      'anodeHighlightMaterial',
      'anodeLowlightMaterial',
      'protectionElementsMaterial',
      'inspectionListElementMaterial',
      'basicMaterial',
      'rulerLineMaterial',
      'lightModelMaterial',
      'criticalAreaMaterial',
      'criticalAreaBoxMaterial',
      'repairBasicMaterial',
      'executedRepairMaterial',
      'doublerExecutedRepairMaterial',
      'cutPreviewMaterial',
      'basicPhysicalMaterial',
      'selectedBasicMaterial',
      'selectedBasicElementMaterial',
      'areaInspecMaterial',
      'selectedAreaInspecMaterial',
      'stiffenerMaterial3d',
      'bracketMaterial3d',
      'flangeMaterial3d',
      'stainlessSteelMaterial',
      'decalMaterial',
      'anomalyGraphicsBasicMaterial',
      'sensorHighlightMaterial',
      'sensorLowlightMaterial',
      'sensorXrayMaterial',
      'sensorMaterial',
      'hatchingMaterial',
      'feaMeshMaterial',
      'femMaterial',
      'structure4DMaterial'
    ];

    materialProperties.forEach(propName => {
      const material = (GraphicsMaterials as any)[propName];
      if (material && material.polygonOffset === true) {
        materials.push({ name: propName, material });
      }
    });

    return materials;
  }

  private addPolygonOffsetControl(materialName: string, material: THREE.Material): void {
    // Initialize debug config for this material
    this.debugConfig[materialName] = {
      enabled: material.polygonOffset || false,
      factor: (material as any).polygonOffsetFactor || 0,
      units: (material as any).polygonOffsetUnits || 0
    };

    const folder = this.polygonOffsetFolder.addFolder(materialName);
    const config = this.debugConfig[materialName];

    // Enabled toggle
    folder.add(config, 'enabled').onChange((value: boolean) => {
      this.updateMaterialProperty(materialName, 'polygonOffset', value);
    });

    // Factor control
    folder.add(config, 'factor', -50, 50, 0.1).onChange((value: number) => {
      this.updateMaterialProperty(materialName, 'polygonOffsetFactor', value);
    });

    // Units control
    folder.add(config, 'units', -50, 50, 0.1).onChange((value: number) => {
      this.updateMaterialProperty(materialName, 'polygonOffsetUnits', value);
    });

    // Add preset buttons
    const presets = folder.addFolder('Presets');
    presets.add({
      'Reset': () => this.resetMaterial(materialName, material)
    }, 'Reset');

    presets.add({
      'Behind (-1)': () => this.applyPreset(materialName, material, -1, -1)
    }, 'Behind (-1)');

    presets.add({
      'Behind (-5)': () => this.applyPreset(materialName, material, -5, -5)
    }, 'Behind (-5)');

    presets.add({
      'Front (+1)': () => this.applyPreset(materialName, material, 1, 1)
    }, 'Front (+1)');

    folder.close();
  }

  private addGlobalControls(): void {
    const globalFolder = this.polygonOffsetFolder.addFolder('Global Controls');

    const globalConfig = {
      enableAll: true,
      disableAll: false,
      factorOffset: 0,
      unitsOffset: 0
    };

    globalFolder.add({
      'Enable All': () => this.toggleAllPolygonOffset(true)
    }, 'Enable All');

    globalFolder.add({
      'Disable All': () => this.toggleAllPolygonOffset(false)
    }, 'Disable All');

    globalFolder.add(globalConfig, 'factorOffset', -10, 10, 0.1).onChange((value: number) => {
      this.applyGlobalFactorOffset(value);
    });

    globalFolder.add(globalConfig, 'unitsOffset', -10, 10, 0.1).onChange((value: number) => {
      this.applyGlobalUnitsOffset(value);
    });

    globalFolder.add({
      'Reset All': () => this.resetAllMaterials()
    }, 'Reset All');

    globalFolder.add({
      'Refresh Materials': () => this.refreshMaterials()
    }, 'Refresh Materials');

    globalFolder.add({
      'Generate Mapping Code': () => this.generateMappingCode()
    }, 'Generate Mapping Code');
  }

  private resetMaterial(materialName: string, material: THREE.Material): void {
    // Get original values - you might want to store these when initializing
    const originalValues = this.getOriginalValues(materialName);

    material.polygonOffset = originalValues.enabled;
    (material as any).polygonOffsetFactor = originalValues.factor;
    (material as any).polygonOffsetUnits = originalValues.units;

    // Update debug config
    this.debugConfig[materialName] = { ...originalValues };

    material.needsUpdate = true;
  }

  private applyPreset(materialName: string, material: THREE.Material, factor: number, units: number): void {
    material.polygonOffset = true;
    (material as any).polygonOffsetFactor = factor;
    (material as any).polygonOffsetUnits = units;

    this.debugConfig[materialName].enabled = true;
    this.debugConfig[materialName].factor = factor;
    this.debugConfig[materialName].units = units;

    material.needsUpdate = true;
  }

  private toggleAllPolygonOffset(enabled: boolean): void {
    Object.keys(this.debugConfig).forEach(materialName => {
      const material = this.getMaterialByName(materialName);
      if (material) {
        material.polygonOffset = enabled;
        this.debugConfig[materialName].enabled = enabled;
        material.needsUpdate = true;
      }
    });
  }

  private applyGlobalFactorOffset(offset: number): void {
    Object.keys(this.debugConfig).forEach(materialName => {
      const material = this.getMaterialByName(materialName);
      if (material) {
        const originalFactor = this.getOriginalValues(materialName).factor;
        (material as any).polygonOffsetFactor = originalFactor + offset;
        this.debugConfig[materialName].factor = originalFactor + offset;
        material.needsUpdate = true;
      }
    });
  }

  private applyGlobalUnitsOffset(offset: number): void {
    Object.keys(this.debugConfig).forEach(materialName => {
      const material = this.getMaterialByName(materialName);
      if (material) {
        const originalUnits = this.getOriginalValues(materialName).units;
        (material as any).polygonOffsetUnits = originalUnits + offset;
        this.debugConfig[materialName].units = originalUnits + offset;
        material.needsUpdate = true;
      }
    });
  }

  private resetAllMaterials(): void {
    Object.keys(this.debugConfig).forEach(materialName => {
      const material = this.getMaterialByName(materialName);
      if (material) {
        this.resetMaterial(materialName, material);
      }
    });
  }

  private getMaterialByName(materialName: string): THREE.Material | null {
    return (GraphicsMaterials as any)[materialName] || null;
  }

  private getOriginalValues(materialName: string): PolygonOffsetDebugConfig {
    // Store original values - you might want to enhance this with actual stored values
    const material = this.getMaterialByName(materialName);
    if (!material) return { enabled: false, factor: 0, units: 0 };

    // Default original values based on common patterns in your code
    const originalValues: { [key: string]: PolygonOffsetDebugConfig } = {
      'inspectionMaterial': { enabled: true, factor: -4, units: -3 },
      'redInspectionMaterial': { enabled: true, factor: -4, units: -3 },
      'yellowInspectionMaterial': { enabled: true, factor: -3.15, units: -3.3 },
      'blueInspectionMaterial': { enabled: true, factor: -3.1, units: -3.2 },
      'greenInspectionMaterial': { enabled: true, factor: -3.05, units: -3.1 },
      'greyInspectionMaterial': { enabled: true, factor: -3, units: -3 },
      'greyAnomalyMaterial': { enabled: true, factor: -2, units: -2 },
      'redAnomalyMaterial': { enabled: true, factor: -2, units: -2 },
      'orangeAnomalyMaterial': { enabled: true, factor: -2, units: -2 },
      'yellowAnomalyMaterial': { enabled: true, factor: -2, units: -2 },
      'blueAnomalyMaterial': { enabled: true, factor: -2, units: -2 },
      'greenAnomalyMaterial': { enabled: true, factor: -2, units: -2 },
      'redRiskMaterial': { enabled: true, factor: -6, units: -6 },
      'orangeRiskMaterial': { enabled: true, factor: -6, units: -6 },
      'yellowRiskMaterial': { enabled: true, factor: -6, units: -6 },
      'greenRiskMaterial': { enabled: true, factor: -6, units: -6 },
      'femMaterial': { enabled: true, factor: 1, units: 0 },
      'feaMeshMaterial': { enabled: true, factor: -10, units: -10 },
      'structure4DMaterial': { enabled: true, factor: 0.5, units: 20 },
    };

    return originalValues[materialName] || {
      enabled: material.polygonOffset || false,
      factor: (material as any).polygonOffsetFactor || 0,
      units: (material as any).polygonOffsetUnits || 0
    };
  }

  public show(): void {
    this.gui.show();
  }

  public hide(): void {
    this.gui.hide();
  }

  public toggle(): void {
    if (this.gui._hidden) {
      this.show();
    } else {
      this.hide();
    }
  }

  public destroy(): void {
    if (this.gui) {
      this.gui.destroy();
    }
  }

  public addCustomMaterial(name: string, material: THREE.Material): void {
    // Store the material reference so updateMaterialProperty can find it
    if (!this.sceneMaterials.has(name)) {
      this.sceneMaterials.set(name, []);
    }
    this.sceneMaterials.get(name)!.push(material);

    if (material.polygonOffset === true) {
      this.addPolygonOffsetControl(name, material);
    }
  }

  public exportSettings(): string {
    const settings: { [key: string]: any } = {};
    Object.keys(this.debugConfig).forEach(materialName => {
      const material = this.getMaterialByName(materialName);
      if (material) {
        settings[materialName] = {
          enabled: material.polygonOffset,
          factor: (material as any).polygonOffsetFactor,
          units: (material as any).polygonOffsetUnits
        };
      }
    });
    return JSON.stringify(settings, null, 2);
  }

  public importSettings(settingsJson: string): void {
    try {
      const settings = JSON.parse(settingsJson);
      Object.keys(settings).forEach(materialName => {
        const material = this.getMaterialByName(materialName);
        const config = settings[materialName];
        if (material && config) {
          this.updateMaterialProperty(materialName, 'polygonOffset', config.enabled);
          this.updateMaterialProperty(materialName, 'polygonOffsetFactor', config.factor);
          this.updateMaterialProperty(materialName, 'polygonOffsetUnits', config.units);

          this.debugConfig[materialName] = { ...config };
        }
      });
    } catch (error) {
      console.error('Error importing settings:', error);
    }
  }

  private updateMaterialProperty(materialName: string, property: string, value: any): void {
    let updatedCount = 0;
    const matchedMaterials: string[] = [];

    // Update the static material
    const staticMaterial = this.getMaterialByName(materialName);
    if (staticMaterial) {
      (staticMaterial as any)[property] = value;
      staticMaterial.needsUpdate = true;
      updatedCount++;
      matchedMaterials.push(`Static: ${staticMaterial.name || materialName}`);
    }

    // Update any scene materials that match this material name
    const sceneMaterials = this.sceneMaterials.get(materialName) || [];
    sceneMaterials.forEach(material => {
      (material as any)[property] = value;
      material.needsUpdate = true;
      updatedCount++;
      matchedMaterials.push(`Scene: ${material.name || 'unnamed'}`);
    });

    // If we have access to the scene, scan for materials with matching names
    if (this.scene) {
      this.scene.traverse((object) => {
        if (object instanceof THREE.Mesh && object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach(mat => {
            if (this.isMatchingMaterial(mat, materialName, staticMaterial)) {
              (mat as any)[property] = value;
              mat.needsUpdate = true;
              updatedCount++;
              matchedMaterials.push(`Traversed: ${mat.name || 'unnamed'} (${mat.constructor.name})`);
            }
          });
        }
      });
    }

    console.log(`🔧 Updated ${property} = ${value} for material: ${materialName}`);
    console.log(`   📊 ${updatedCount} materials affected:`);
    matchedMaterials.forEach(name => console.log(`     - ${name}`));

    if (updatedCount === 0) {
      console.warn(`⚠️  No materials found for: ${materialName}. Check material name mapping.`);
    }
  }

  private isMatchingMaterial(sceneMaterial: THREE.Material, debugMaterialName: string, staticMaterial: THREE.Material | null): boolean {
    const sceneName = sceneMaterial.name || '';

    // Direct name match
    if (sceneName === debugMaterialName) {
      console.debug(`🎯 Direct name match: ${sceneName} === ${debugMaterialName}`);
      return true;
    }

    // Check if this property name has mapped scene names
    const mappedNames = this.materialNameMapping[debugMaterialName];
    if (mappedNames) {
      for (const mappedName of mappedNames) {
        if (sceneName === mappedName || sceneName.toLowerCase() === mappedName.toLowerCase()) {
          console.debug(`🎯 Mapped name match: ${sceneName} matches ${mappedName} for ${debugMaterialName}`);
          return true;
        }
      }
    }

    // Color-based matching for materials with similar properties (fallback)
    if (staticMaterial && this.isSameMaterialType(sceneMaterial, staticMaterial)) {
      console.debug(`🎯 Type-based match: ${sceneName} (${sceneMaterial.constructor.name}) matches ${debugMaterialName} via type/color similarity`);
      return true;
    }

    return false;
  }

  private isSameMaterialType(material1: THREE.Material, material2: THREE.Material | null): boolean {
    if (!material2) return false;

    // Check if materials have similar properties (same type, similar colors, etc.)
    if (material1.constructor !== material2.constructor) return false;

    // For materials with textures, compare texture properties instead of colors
    if (material1 instanceof THREE.MeshBasicMaterial && material2 instanceof THREE.MeshBasicMaterial) {
      // If both have maps (textures), don't do color comparison as it can cause false matches
      if ((material1 as any).map && (material2 as any).map) {
        // Only match if they're the exact same texture reference
        return (material1 as any).map === (material2 as any).map;
      }
      // If neither has a map, compare colors
      if (!(material1 as any).map && !(material2 as any).map) {
        return material1.color?.equals(material2.color);
      }
      // If one has a map and the other doesn't, they're different
      return false;
    }

    // For standard materials, check color similarity
    if (material1 instanceof THREE.MeshStandardMaterial && material2 instanceof THREE.MeshStandardMaterial) {
      // Similar logic for standard materials
      if ((material1 as any).map && (material2 as any).map) {
        return (material1 as any).map === (material2 as any).map;
      }
      if (!(material1 as any).map && !(material2 as any).map) {
        return material1.color?.equals(material2.color);
      }
      return false;
    }

    return false;
  }

  public setScene(scene: THREE.Scene): void {
    this.scene = scene;
    this.scanSceneForMaterials();
  }

  public scanSceneForMaterials(): void {
    if (!this.scene) return;

    this.sceneMaterials.clear();
    const foundMaterials: string[] = [];
    const allMaterials: string[] = [];
    const mappingResults: { [propName: string]: string[] } = {};

    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach(material => {
          const name = material.name || `Material_${material.id}`;
          allMaterials.push(name);

          if (material.polygonOffset === true || (material as any).polygonOffsetFactor !== undefined) {
            if (!this.sceneMaterials.has(name)) {
              this.sceneMaterials.set(name, []);
              foundMaterials.push(name);
            }
            this.sceneMaterials.get(name)!.push(material);

            // Add control if not already added
            if (!this.debugConfig[name]) {
              this.addPolygonOffsetControl(name, material);
            }
          }
        });
      }
    });

    // Check which property names match which scene materials
    Object.keys(this.materialNameMapping).forEach(propName => {
      const staticMaterial = this.getMaterialByName(propName);
      const matchedSceneMaterials: string[] = [];

      foundMaterials.forEach(sceneName => {
        const sceneMaterial = this.sceneMaterials.get(sceneName)?.[0];
        if (sceneMaterial && this.isMatchingMaterial(sceneMaterial, propName, staticMaterial)) {
          matchedSceneMaterials.push(sceneName);
        }
      });

      if (matchedSceneMaterials.length > 0) {
        mappingResults[propName] = matchedSceneMaterials;
      }
    });

    console.log(`🔍 Scene Material Scan Results:`);
    console.log(`  - Found ${this.sceneMaterials.size} materials with polygon offset:`);
    foundMaterials.forEach(name => console.log(`    ✅ ${name}`));

    console.log(`\n📋 Property → Scene Material Mapping:`);
    Object.keys(mappingResults).forEach(propName => {
      console.log(`  🔗 ${propName} → [${mappingResults[propName].join(', ')}]`);
    });

    // Show unmapped scene materials
    const mappedSceneMaterials = new Set(Object.values(mappingResults).flat());
    const unmappedMaterials = foundMaterials.filter(name => !mappedSceneMaterials.has(name));
    if (unmappedMaterials.length > 0) {
      console.log(`\n⚠️  Unmapped scene materials (add these to materialNameMapping):`);
      unmappedMaterials.forEach(name => console.log(`    ❓ ${name}`));
    }

    console.log(`\n📊 All materials in scene (${allMaterials.length} total):`);
    const uniqueMaterials = [...new Set(allMaterials)];
    uniqueMaterials.forEach(name => {
      const hasPolygonOffset = foundMaterials.includes(name);
      const isMapped = mappedSceneMaterials.has(name);
      const status = hasPolygonOffset ? (isMapped ? '🔗' : '❓') : '❌';
      console.log(`    ${status} ${name}`);
    });

    if (foundMaterials.length === 0) {
      console.warn(`⚠️  No materials with polygon offset found in scene. You may need to:`);
      console.warn(`   1. Load your 3D assets first`);
      console.warn(`   2. Set polygonOffset: true on your materials`);
      console.warn(`   3. Click "Refresh Materials" after loading assets`);
    }
  }

  public refreshMaterials(): void {
    this.scanSceneForMaterials();
  }

  public addMaterialMapping(propName: string, sceneNames: string[]): void {
    this.materialNameMapping[propName] = sceneNames;
    console.log(`Added mapping: ${propName} → [${sceneNames.join(', ')}]`);
  }

  public generateMappingCode(): string {
    if (!this.scene) return 'No scene available';

    const foundMaterials: string[] = [];
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach(material => {
          const name = material.name || `Material_${material.id}`;
          if ((material.polygonOffset === true || (material as any).polygonOffsetFactor !== undefined) && !foundMaterials.includes(name)) {
            foundMaterials.push(name);
          }
        });
      }
    });

    let code = '// Add these to materialNameMapping:\n';
    foundMaterials.forEach(sceneName => {
      // Try to guess the property name
      const propName = sceneName.charAt(0).toLowerCase() + sceneName.slice(1);
      code += `'${propName}': ['${sceneName}'],\n`;
    });

    console.log(code);
    return code;
  }
}
